import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiCaller } from '../utils/ApiUtilities';
import { useUser } from '../contexts/UserContext';
import { getPurchaseStatusUrl, getCancelPurchaseUrl } from '../utils/UrlUtilities';

/**
 * Landing page after the gateway. Reads the status the gateway redirected with, refreshes
 * entitlements (navbar/banner), and confirms the true status server-side via the owner-scoped
 * purchase-status endpoint. With Stripe the grant lands via the webhook, which can trail the
 * browser redirect by a moment, so we poll purchase-status until it settles.
 */
export default function SubscriptionResult() {
    const [params] = useSearchParams();
    const CallApi = useApiCaller();
    const { refreshSubscription } = useUser();

    const urlStatus = (params.get('status') || '').toLowerCase();
    const tranId = params.get('tran_id') || '';
    const [confirmed, setConfirmed] = useState(null); // server-confirmed txn status
    const [polling, setPolling] = useState(false);

    useEffect(() => {
        // Self-contained per-mount so React StrictMode's double-invoke (mount → cleanup →
        // mount) can't leave polling stuck: the first pass is cancelled by its own cleanup
        // and the second pass runs to completion.
        refreshSubscription();
        // Cancelled return: no charge was attempted. Tell the server to mark the still-PENDING
        // purchase CANCELLED (so it doesn't linger as Pending), then stop — no polling needed.
        if (urlStatus === 'cancelled') {
            if (tranId) {
                CallApi({ url: getCancelPurchaseUrl(tranId), method: 'POST', token: true, silent: true });
            }
            return;
        }
        if (!tranId) return;

        let cancelled = false;
        setPolling(true);
        (async () => {
            // Poll for up to ~12s (8 tries × 1.5s) for the webhook/callback to finalize the grant.
            for (let attempt = 0; attempt < 8 && !cancelled; attempt++) {
                const res = await CallApi({
                    url: getPurchaseStatusUrl(tranId), method: 'GET', token: true, silent: true,
                });
                const status = res.success ? (res.data?.status || null) : null;
                if (status && status !== 'PENDING') {
                    if (!cancelled) {
                        setConfirmed(status);
                        refreshSubscription();
                    }
                    break;
                }
                await new Promise(r => setTimeout(r, 1500));
            }
            if (!cancelled) setPolling(false);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Prefer the server-confirmed status once we have it.
    const success = confirmed ? confirmed === 'SUCCESS' : urlStatus === 'success' && !polling;
    const cancelled = !confirmed && urlStatus === 'cancelled';

    if (polling && !confirmed) {
        return (
            <div className="container py-5 text-center" style={{ maxWidth: 520 }}>
                <div className="spinner-border text-primary mb-3" role="status" />
                <h4 className="mb-2">Confirming your payment…</h4>
                <p className="text-muted">This only takes a moment.</p>
            </div>
        );
    }

    return (
        <div className="container py-5 text-center" style={{ maxWidth: 520 }}>
            {success ? (
                <div className="alert alert-success">
                    <h4 className="mb-2">✅ Payment successful</h4>
                    <p className="mb-3">Your plan is now active. Enjoy!</p>
                    <Link to="/" className="btn btn-primary btn-sm me-2">Go to home</Link>
                    <Link to="/subscription" className="btn btn-outline-secondary btn-sm">Manage subscription</Link>
                </div>
            ) : cancelled ? (
                <div className="alert alert-warning">
                    <h4 className="mb-2">Payment cancelled</h4>
                    <p className="mb-3">No charge was made and your plan is unchanged.</p>
                    <Link to="/subscription" className="btn btn-outline-secondary btn-sm">Back to plans</Link>
                </div>
            ) : (
                <div className="alert alert-danger">
                    <h4 className="mb-2">Payment failed</h4>
                    <p className="mb-3">Something went wrong and your plan was not changed. Please try again.</p>
                    <Link to="/subscription" className="btn btn-outline-secondary btn-sm">Back to plans</Link>
                </div>
            )}
        </div>
    );
}
